import { createContext, useContext, useCallback, useState, type ReactNode } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { cn } from '@/utils'
import { recordError } from '@/services/feedbackContext/capture'
import { useHelpFeedbackBridge } from '@/stores/helpFeedbackBridge'
import { TELL_US } from '@/components/helpFeedback/helpFeedbackText'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  type: ToastType
  title: string
  description?: string
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

/** The toast context, or null outside a provider (a page whose tests mount none). */
export function useOptionalToast() {
  return useContext(ToastContext) ?? null
}

const icons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

const styles: Record<ToastType, string> = {
  success: 'border-success/20 bg-success/10 text-success',
  error: 'border-destructive/20 bg-destructive/10 text-destructive',
  warning: 'border-warning/20 bg-warning/10 text-warning-strong',
  info: 'border-primary/20 bg-primary/10 text-primary',
}

/**
 * The action an error toast with none of its own gets while the Help & feedback panel is mounted
 * and offered (SA-1306 spec §2.4). The provider sits above the panel's host, so it asks through the
 * bridge store rather than the hook.
 */
function tellUsAction(toast: Toast): Toast['action'] {
  const message = [toast.title, toast.description].filter(Boolean).join(' ')
  return { label: TELL_US, onClick: () => useHelpFeedbackBridge.getState().requestOpen({ failure: { message } }) }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const helpReady = useHelpFeedbackBridge((s) => s.hostReady)

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    // SA-1304: an error toast is what the person saw go wrong.
    if (toast.type === 'error') {
      recordError('toast', [toast.title, toast.description].filter(Boolean).join(' '))
    }
    setToasts((prev) => [...prev, { ...toast, id }])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const Icon = icons[toast.type]
          const action = toast.action ?? (toast.type === 'error' && helpReady ? tellUsAction(toast) : undefined)
          return (
            <ToastPrimitive.Root
              key={toast.id}
              duration={toast.duration || 5000}
              onOpenChange={(open) => {
                if (!open) removeToast(toast.id)
              }}
              className={cn(
                'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-lg border p-4 shadow-lg transition-all',
                'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none',
                'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
                'bg-card border-border'
              )}
            >
              <div className="flex items-start gap-3">
                <div className={cn('p-1 rounded-full', styles[toast.type])}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="grid gap-1">
                  <ToastPrimitive.Title className="text-sm font-semibold text-foreground">
                    {toast.title}
                  </ToastPrimitive.Title>
                  {toast.description && (
                    <ToastPrimitive.Description className="text-sm text-muted-foreground">
                      {toast.description}
                    </ToastPrimitive.Description>
                  )}
                  {action && (
                    <ToastPrimitive.Action altText={action.label} asChild>
                      <button type="button" className="justify-self-start text-sm font-medium text-primary underline-offset-2 hover:underline"
                        onClick={action.onClick}>
                        {action.label}
                      </button>
                    </ToastPrimitive.Action>
                  )}
                </div>
              </div>
              <ToastPrimitive.Close
                className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport className="fixed top-0 right-0 z-100 flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-[420px]" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

export function Toaster() {
  return <ToastProvider>{null}</ToastProvider>
}
